import { useTour as useReactTour } from "@reactour/tour"
import { useMemo, useEffect, useCallback, useState } from "react"
import { enableBodyScroll } from "body-scroll-lock"
import {
  ACTIVITY_TYPES,
  ACTIVITY_VIEW_MODES,
} from "pages/student/activity/common/constants"
import {
  ExplanationGetStarted,
  ExplanationVideo,
  PictureQuestionGetStarted,
  MatchingPictureGetStarted,
  ListenAndChooseGetStarted,
  FindThePictureGetStarted,
  MatchingGetStateted,
  ScrambledSentenceGetStarted,
  SoackedManGetStarted,
  WordsPronounciationGetStarted,
  SentencePronounciationGetStarted,
  InteractionsGetStated,
  FillingTheBlanksGetStated,
  FillingTheBlanksWriteGetStated,
  PictureAndQuestionWriteGetStarted,
  DictationGetStarted,
  OralComprehensionGetStarted,
  TextComprehensionGetStarted,
  RecordingComprehensionGetStarted,
  JustWriteGetStarted,
  JustWriteMultipleGetStarted,
  InterviewGetStarted,
  JustTalkGetStarted,
  EssayGetStarted,
  MultipleQuestionsGetStarted,
  ComparativeEssayGetStarted,
  WrittenComprehensionGetStarted,
  ReadingOutloudGetStarted,
  ListenAndFindThePictureGetStarted,
} from "components/ActivityContent/Activity.GetStarted"
import { useAuthContext } from "contexts/AuthContext"
import useApi from "modules/getStarted"
import secureLocalStorage from "react-secure-storage"
import { LOCAL_STORAGE_PREFIX } from "../common/constants"

const TEST_TOUR_ENABLED = false

const getViewMode = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const view = parseInt(queryParams.get("view")) || 0
  // if view is a valid view mode
  if (Object.values(ACTIVITY_VIEW_MODES).includes(view)) {
    return view
  }
  return ACTIVITY_VIEW_MODES.STUDENT
}

export const isTourForced = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const started = parseInt(queryParams.get("started")) || 0
  return started === 1
}

export const isTourDisabled = () => {
  // if domain is review.smasheducation.com then tour is disabled by default
  const defaultValue =
    window.location.hostname === "review.smasheducation.com" ? 1 : 0
  const queryParams = new URLSearchParams(window.location.search)
  const disable =
    queryParams.get("tutorialDisabled") === null
      ? defaultValue
      : parseInt(queryParams.get("tutorialDisabled"))
  return disable === 1
}

export const ACTIVITY_ACTIONS = {
  [ACTIVITY_TYPES.WORDS_TO_REMEMBER]: "actions_get_started_words_to_remember",
  [ACTIVITY_TYPES.PICTURE_AND_QUESTION]:
    "actions_get_started_picture_and_question",
  [ACTIVITY_TYPES.MATCHING]: "actions_get_started_matching",
  [ACTIVITY_TYPES.LISTEN_AND_CHOOSE]: "actions_get_started_listen_and_choose",
  [ACTIVITY_TYPES.EXPLANATION]: "actions_get_started_explanation",
  [ACTIVITY_TYPES.VIDEO_EXPLANATION]: "actions_get_started_explanation_video",
  [ACTIVITY_TYPES.FILLING_THE_BLANKS]: "actions_get_started_filling_the_blanks",
  [ACTIVITY_TYPES.SCRAMBLED_SENTENCES]:
    "actions_get_started_scrambled_sentences",
  [ACTIVITY_TYPES.DICTATION]: "actions_get_started_dictation",
  [ACTIVITY_TYPES.TEXT_COMPREHENSION]: "actions_get_started_text_comprehension",
  [ACTIVITY_TYPES.WRITTEN_COMPREHENSION]:
    "actions_get_started_written_comprehension",
  [ACTIVITY_TYPES.JUST_WRITE]: "actions_get_started_just_write",
  [ACTIVITY_TYPES.WORDS_PRONOUNCIATION]:
    "actions_get_started_words_pronounciation",
  [ACTIVITY_TYPES.SENTENCE_PRONOUNCIATION]:
    "actions_get_started_sentence_pronounciation",
  [ACTIVITY_TYPES.MATCHING_AUDIOS]: "actions_get_started_matching_audio",
  [ACTIVITY_TYPES.READING_OUTLOUD]: "actions_get_started_reading_outloud",
  [ACTIVITY_TYPES.JUST_TALK]: "actions_get_started_just_talk",
  [ACTIVITY_TYPES.WRITTEN_COMPREHENSION]:
    "actions_get_started_written_comprension",
  [ACTIVITY_TYPES.ORAL_COMPREHENSION_LONG]:
    "actions_get_started_oral_comprension_long",
  [ACTIVITY_TYPES.FILLING_THE_BLANKS_WRITE]:
    "actions_get_started_filling_the_blanks_write",
  [ACTIVITY_TYPES.INTERVIEW]: "actions_get_started_interview",
  [ACTIVITY_TYPES.FIND_THE_PICTURE]: "actions_get_started_find_the_picture",
  [ACTIVITY_TYPES.LISTEN_AND_FIND_THE_PICTURE]:
    "actions_get_started_listen_find_the_picture",
  [ACTIVITY_TYPES.SOACKED_MAN]: "actions_get_started_soacked_man",
  [ACTIVITY_TYPES.MATCHING_PICTURES]: "actions_get_started_matching_pictures",
  [ACTIVITY_TYPES.RECORDING_COMPREHENSION]:
    "actions_get_started_recording_comprehension",
  [ACTIVITY_TYPES.COMPARATIVE_ESSAY]: "actions_get_started_comparative_essay",
  [ACTIVITY_TYPES.JUST_WRITE_MULTIPLE]:
    "actions_get_started_just_write_multiple",
  [ACTIVITY_TYPES.JUST_TALK_MULTIPLE]: "actions_get_started_just_talk_multiple",
  [ACTIVITY_TYPES.INTERACTIONS]: "actions_get_started_interactions",
  [ACTIVITY_TYPES.PICTURE_AND_QUESTION_WRITE]:
    "actions_get_started_picture_and_question_write",
  [ACTIVITY_TYPES.ESSAY]: "actions_get_started_essay",
  [ACTIVITY_TYPES.MULTIPLE_QUESTIONS]: "actions_get_started_multiple_questions",
}

export const GET_STARTED_ACTIONS = [
  {
    code: "actions_get_started_start_tutorial",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_missions_overview",
    path: "/missions",
    variations: ["forced", "notForced"],
  },
  {
    code: "actions_missions_modules",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_first_activity_graded",
    path: "/activity",
    variations: ["forced"],
  },
  {
    code: "actions_second_activity_graded",
    path: "/activity",
    variations: ["forced"],
  },
  {
    code: "actions_tools_explanation_overview",
    path: "/activity",
    variations: ["forced"],
  },
  {
    code: "actions_tools_answer_overview",
    path: "/activity",
    variations: ["forced"],
  },
  {
    code: "actions_activity_speech",
    path: "/activity",
    variations: ["forced"],
  },
  {
    code: "actions_activity_graded_by_teacher",
    path: "/activity",
    variations: ["forced"],
  },
  {
    code: "actions_notifications_new_grade",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_teachergradedactivities_overview",
    path: "/teacherGrades",
    variations: ["forced", "notForced"],
  },
  {
    code: "actions_missions_new_grade",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_tools_notes_overview",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_tools_library_overview",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_grades_overview",
    path: "/reports",
    variations: ["forced", "notForced"],
  },
  {
    code: "actions_tools_support_overview",
    path: "/missions",
    variations: ["forced"],
  },
  {
    code: "actions_announcements_overview",
    path: "/announcements",
    variations: ["forced", "notForced"],
  },
  {
    code: "actions_smashgame_overview",
    path: "/professor/smash/v2/game",
    variations: ["forced", "notForced"],
  },
  {
    code: "actions_get_started_end_tutorial",
    path: "/missions",
    variations: ["forced"],
  },
]
export const GET_STARTED = {
  FIRST_MODULE_GRAMMAR: "actions_first_grammar_module",
  SECOND_MODULE_GRAMMAR: "actions_second_grammar_module",
  ACTIVITY_GRADED: "actions_first_activity_graded",
  ACTIVITY_RETAKE: "actions_second_activity_graded",
  EXPLANATION_WTR: "ExplanationWTRTour",
  EXPLANATION_GRAMMAR: "ExplanationGrammarTour",
  ANSWER_TOOL: "AnswerToolTour",
  NOTES_TOOL: "NotesToolTour",
  EXPORT_NOTES_TOOL: "ExportNotesToolTour",
  LIBRARY_TOOL: "LibraryToolTour",
  CONTENT_ERROR_TOOL: "ContentErrorToolTour",
  GLINTCH_SYSTEM_TOOL: "GlitchSystemToolTour",
  MATCHING_ACTIVITY: "MatchingActivityTour",
  EXPLANATION_ACTIVITY: "ExplanationActivityTour",
  VIDEO_ACTIVITY: "VideoActivityTour",
  MATCHING_PICTURES_ACTIVITY: "MatchingPicturesActivityTour",
  LISTEN_AND_CHOOSE_ACTIVITY: "ListenAndChooseActivityTour",
  FILLING_THE_BLANKS: "FillingTheBlanksActivityTour",
  ORAL_COMPREHENSION: "OralComprehensionActivityTour",
  JUST_WRITE: "JustWriteActivityTour",
  JUST_TALK: "JustTalkActivityTour",
  SCRAMBLED_SENTENCES: "ScrambledSentencesActivityTour",
  SPEECH_SETTINGS: "actions_activity_speech",
  SPEECH_RESULT_CORRECT: "SpeechResultCorrectTour",
  END_MODULE: "actions_first_module_end",
  SOACKED_MAN_ACTIVITY: "SoackedManActivityTour",
  GRADED_BY_TEACHER: "GradedByTeacherTour",
  INTERVIEW_ACTIVITY: "InterviewTour",
  INTERVIEW_ACTIVITY_2: "InterviewFinishedTour",
  INTERACTIONS_ACTIVITY: "InteractionTour",
  INTERACTIONS_ACTIVITY_2: "InteractionFinishedTour",
  PICTURE_AND_QUESTION_WRITE_ACTIVITY: "PictureAndQuestionWriteTour",
  PICTURE_AND_QUESTION_WRITE: "PictureAndQuestionWriteAnswerTour",
  READING_OUTLOUD_ACTIVITY: "ReadingOutloudTour",
  PICTURE_AND_QUESTION_TOOL: "PictureAndQuestionToolTour",
  ACTIVITY_GRADED_BY_TEACHER: "ActivityGradedByTeacherTour",
  PICTURE_AND_QUESTION_ACTIVITY: "PictureQuestionTour",
  DICTATION_ACTIVITY: "DictationTour",
  ESSAY_ACTIVITY: "EssayTour",
  FILLING_THE_BLANKS_WRITE_ACTIVITY: "FillingTheBlanksWriteTour",
  JUST_WRITE_MULTIPLE_ACTIVITY: "JustWriteMultipleTour",
  MULTIPLE_QUESTIONS_ACTIVITY: "MultipleQuestionsTour",
  RECORDING_COMPREHENSION_ACTIVITY: "RecordingComprehensionTour",
  TEXT_COMPREHENSION_ACTIVITY: "TextComprehensionTour",
  WORDS_TO_REMEMBER_ACTIVITY: "WordsToRememberTour",
  FIND_THE_PICTURE_ACTIVITY: "FindThePictureTour",
  LISTEN_AND_FIND_THE_PICTURE_ACTIVITY: "ListenAndFindThePictureTour",
  SENTENCE_PRONOUNCIATION_ACTIVITY: "SentencePronounciationTour",
  WORDS_PRONOUNCIATION_ACTIVITY: "WordsPronounciationTour",
  ACTIVITY_GRADED_WRONG: "ActivityGradedWrongTour",
  SPEECH_WRONG: "SpeechWrongTour",
}

export const GET_STARTED_ACTIVITY = {
  [ACTIVITY_TYPES.WORDS_TO_REMEMBER]: SentencePronounciationGetStarted,
  [ACTIVITY_TYPES.PICTURE_AND_QUESTION]: PictureQuestionGetStarted,
  [ACTIVITY_TYPES.MATCHING]: MatchingGetStateted,
  [ACTIVITY_TYPES.LISTEN_AND_CHOOSE]: ListenAndChooseGetStarted,
  [ACTIVITY_TYPES.EXPLANATION]: ExplanationGetStarted,
  [ACTIVITY_TYPES.FILLING_THE_BLANKS]: FillingTheBlanksGetStated,
  [ACTIVITY_TYPES.SCRAMBLED_SENTENCES]: ScrambledSentenceGetStarted,
  [ACTIVITY_TYPES.DICTATION]: DictationGetStarted,
  [ACTIVITY_TYPES.WRITTEN_COMPREHENSION]: WrittenComprehensionGetStarted,
  [ACTIVITY_TYPES.JUST_WRITE]: JustWriteGetStarted,
  [ACTIVITY_TYPES.VIDEO_EXPLANATION]: ExplanationVideo,
  [ACTIVITY_TYPES.MATCHING_AUDIOS]: MatchingGetStateted,
  [ACTIVITY_TYPES.WORDS_PRONOUNCIATION]: WordsPronounciationGetStarted,
  [ACTIVITY_TYPES.SENTENCE_PRONOUNCIATION]: SentencePronounciationGetStarted,
  [ACTIVITY_TYPES.READING_OUTLOUD]: ReadingOutloudGetStarted,
  [ACTIVITY_TYPES.JUST_TALK]: JustTalkGetStarted,
  [ACTIVITY_TYPES.TEXT_COMPREHENSION]: TextComprehensionGetStarted,
  [ACTIVITY_TYPES.ORAL_COMPREHENSION_LONG]: OralComprehensionGetStarted,
  [ACTIVITY_TYPES.ORAL_COMPREHENSION_SHORT]: OralComprehensionGetStarted,
  [ACTIVITY_TYPES.FILLING_THE_BLANKS_WRITE]: FillingTheBlanksWriteGetStated,
  [ACTIVITY_TYPES.INTERVIEW]: InterviewGetStarted,
  [ACTIVITY_TYPES.FIND_THE_PICTURE]: FindThePictureGetStarted,
  [ACTIVITY_TYPES.LISTEN_AND_FIND_THE_PICTURE]:
    ListenAndFindThePictureGetStarted,
  [ACTIVITY_TYPES.SOACKED_MAN]: SoackedManGetStarted,
  [ACTIVITY_TYPES.MATCHING_PICTURES]: MatchingPictureGetStarted,
  [ACTIVITY_TYPES.RECORDING_COMPREHENSION]: RecordingComprehensionGetStarted,
  [ACTIVITY_TYPES.COMPARATIVE_ESSAY]: ComparativeEssayGetStarted, // depends on the answer type
  [ACTIVITY_TYPES.JUST_WRITE_MULTIPLE]: JustWriteMultipleGetStarted,
  [ACTIVITY_TYPES.JUST_TALK_MULTIPLE]: [],
  [ACTIVITY_TYPES.INTERACTIONS]: InteractionsGetStated,
  [ACTIVITY_TYPES.PICTURE_AND_QUESTION_WRITE]:
    PictureAndQuestionWriteGetStarted,
  [ACTIVITY_TYPES.ESSAY]: EssayGetStarted,
  [ACTIVITY_TYPES.MULTIPLE_QUESTIONS]: MultipleQuestionsGetStarted,
}

/**
 * Checks whether a tour step selector is in the DOM or not
 */
export function isElementInDOM(step) {
  const nodes = document.querySelectorAll(step.selector)
  return nodes.length > 0
}

export default function useTour(localStorageKey, initSteps) {
  const viewMode = getViewMode()
  const { setIsOpen, setCurrentStep, setSteps, isOpen, currentStep, steps } =
    useReactTour()
  const [tourOpened, setTourOpened] = useState(false)
  const [forceTour, setForceTour] = useState(false)
  const { userData, tourSkiped, setTourSkiped } = useAuthContext()
  const { isGetStartedViewed, setGetStartedViewed, listGetStartedViewed } =
    useApi()
  // const [ignoreViewMode, setIgnoreViewMode] = useState(false)

  useEffect(() => {
    setForceTour(isTourForced())
    return () => {
      setForceTour(false)
    }
  }, [])

  useEffect(() => {
    setIsOpen(false)
    return () => {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (tourSkiped) {
      setTourOpened(false)
      setTourSkiped(false)
    }
  }, [tourSkiped])

  useEffect(() => {
    if (!initSteps) return
    setIsOpen(false)
    setCurrentStep(0)
    setSteps(initSteps)
    enableBodyScroll()
    setTourOpened(false)
  }, [initSteps])

  useEffect(() => {
    if (!isOpen) return
    setTourOpened(true)
  }, [isOpen])

  useEffect(() => {
    // Reset tour if closed
    // if (isOpen === false) setCurrentStep(0)
    if (isOpen && steps.length > 0) {
      secureLocalStorage.setItem(
        `current-tour_${userData?.userId}`,
        steps[0]?.id
      )
    }
  }, [isOpen, currentStep, steps])

  const isSameSteps = useCallback((stepsA = [], stepsB = []) => {
    if (stepsA.length !== stepsB.length) {
      // loop throught the one with less length
      const lessLength = stepsA.length < stepsB.length ? stepsA : stepsB
      for (let i = 0; i < lessLength.length; i++) {
        if (stepsA[i]?.id !== stepsB[i]?.id) return false
      }
    } else {
      for (let i = 0; i < stepsA.length; i++) {
        if (stepsA[i]?.id !== stepsB[i]?.id) return false
      }
    }
    return true
  }, [])

  const isGetStartedViewedCallback = useCallback(
    async (name, controller) => {
      const tutorialEnabled = userData?.allowTutorials ?? true
      if (isTourDisabled() || !tutorialEnabled) {
        // console.log("is disabled")
        return true
      }
      if (TEST_TOUR_ENABLED) return false
      if (
        viewMode !== ACTIVITY_VIEW_MODES.STUDENT &&
        viewMode !== ACTIVITY_VIEW_MODES.EXPLANATION
      ) {
        return true
      }
      try {
        // console.log("isGetStaredViewed", `${name}`)
        const response = await isGetStartedViewed(`${name}`, controller)
        return response
      } catch (error) {
        console.log("~ isGetStartedViewedCallback ~ error:", error)
        return true
      }
      // const tourViewed = localStorage.getItem(`${name}${ext}_${userData?.userId}`)
      // return tourViewed && tourViewed !== null
    },
    [userData]
  )

  const getGetStartedsViewed = useCallback(async () => {
    const tutorialEnabled = userData?.allowTutorials ?? true
    if (isTourDisabled() || !tutorialEnabled) {
      // list all activities actions in array
      const forzedGetStarted = GET_STARTED_ACTIONS.map((action) => action.code)
      const activitiesActions = Object.values(ACTIVITY_ACTIONS)
      const getStatedActions = Object.values(GET_STARTED)
      const joinedActions = [
        ...forzedGetStarted,
        ...activitiesActions,
        ...getStatedActions,
      ]
      return {
        completedActions: joinedActions,
      }
    }
    if (TEST_TOUR_ENABLED) {
      return {
        completedActions: [],
      }
    }
    if (
      viewMode !== ACTIVITY_VIEW_MODES.STUDENT &&
      viewMode !== ACTIVITY_VIEW_MODES.EXPLANATION
    ) {
      const forzedGetStarted = GET_STARTED_ACTIONS.map((action) => action.code)
      const activitiesActions = Object.values(ACTIVITY_ACTIONS)
      const getStatedActions = Object.values(GET_STARTED)
      const joinedActions = [
        ...forzedGetStarted,
        ...activitiesActions,
        ...getStatedActions,
      ]
      return {
        completedActions: joinedActions,
      }
    }
    const list = await listGetStartedViewed()
    return list
  }, [userData])

  const setGetStartedViewedCallback = useCallback(async (name) => {
    try {
      // console.log("setGetStartedViewed", `${name}`)
      const response = await setGetStartedViewed(`${name}`)
      console.log(response)
      return true
    } catch (error) {
      console.log("~ setGetStartedViewedCallback ~ error:", error)
      return false
    }
  }, [])

  const superIsOpen = useCallback((forceIsOpen) => {
    setIsOpen(forceIsOpen)
  }, [])

  const setIsOpenCallback = useCallback(
    (_isOpen) => {
      if (isOpen === true && _isOpen === true) return
      if (
        _isOpen &&
        viewMode !== ACTIVITY_VIEW_MODES.STUDENT &&
        viewMode !== ACTIVITY_VIEW_MODES.EXPLANATION
      ) {
        return
      }
      if (!localStorageKey) {
        setIsOpen(_isOpen)
        return
      }
      const tourViewed = localStorage.getItem(
        `${LOCAL_STORAGE_PREFIX}${localStorageKey}`
      )
      if (tourViewed) {
        if (!_isOpen) setIsOpen(_isOpen)
        return
      }
      if (_isOpen) {
        localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${localStorageKey}`, "1")
      }
      setIsOpen(_isOpen)
    },
    [setIsOpen, viewMode, isOpen]
  )

  const onNextStep = useCallback(() => {
    if (currentStep === steps.length - 1) {
      setIsOpen(false)
      setCurrentStep(0)
      setSteps([])
      localStorage.removeItem(
        `${LOCAL_STORAGE_PREFIX}current-tour_${userData?.userId}`
      )
    } else {
      setCurrentStep((currentStep) => currentStep + 1)
    }
  }, [currentStep, steps])

  const startTour = useCallback(
    ({ activityTypeId, delay = 100, startAt = 0, tourOpened = null }) => {
      setIsOpen(false)
      setCurrentStep(startAt)
      const activitySteps = GET_STARTED_ACTIVITY[activityTypeId]
      const filteredSteps =
        activitySteps?.filter((step) => isElementInDOM(step)) || []
      setSteps(filteredSteps)
      if (filteredSteps?.length > 0) {
        secureLocalStorage.setItem(
          `current-tour_${userData?.userId}`,
          filteredSteps[0]?.id
        )
      }
      const timeout = setTimeout(() => {
        const activitySteps = GET_STARTED_ACTIVITY[activityTypeId]
        const filteredSteps =
          activitySteps?.filter((step) => isElementInDOM(step)) || []
        // console.log(filteredSteps.length, "steps")
        if (filteredSteps.length === 0) {
          console.log("no steps to show")
          setIsOpen(false)
          setTourOpened(false)
          return
        }
        setSteps(filteredSteps)
        localStorage.setItem(
          `${LOCAL_STORAGE_PREFIX}current-tour_${userData?.userId}`,
          filteredSteps[0]?.id
        )
        setIsOpen(true)
      }, delay)
      return () => clearTimeout(timeout)
    },
    [viewMode]
  )

  const setStepsCallback = useCallback(
    (steps, ignore = false, filter = false) => {
      // const filteredSteps = !ignore
      //   ? steps?.filter((step) => isElementInDOM(step)) || []
      //   : steps
      // if (filteredSteps.length === 0) {
      //   console.log("no steps to show")
      // }
      const filteredSteps = filter
        ? steps?.filter((step) => isElementInDOM(step)) || []
        : steps
      if (filteredSteps.length === 0) {
        console.log("no steps to show")
      }
      setSteps(filteredSteps)
      if (steps.length > 0) {
        localStorage.setItem(
          `${LOCAL_STORAGE_PREFIX}current-tour`,
          steps[0]?.id
        )
      } else {
        localStorage.removeItem(
          `${LOCAL_STORAGE_PREFIX}current-tour_${userData?.userId}`
        )
      }
    },
    []
  )

  const setStepsByActivityType = useCallback((activityTypeId) => {
    const activitySteps = GET_STARTED_ACTIVITY[activityTypeId]
    const filteredSteps =
      activitySteps?.filter((step) => isElementInDOM(step)) || []
    if (filteredSteps.length === 0) {
      console.log("no steps to show")
    }
    setSteps(filteredSteps)
    if (filteredSteps.length > 0) {
      localStorage.setItem(
        `${LOCAL_STORAGE_PREFIX}current-tour_${userData?.userId}`,
        filteredSteps[0]?.id
      )
    } else {
      localStorage.removeItem(
        `${LOCAL_STORAGE_PREFIX}current-tour_${userData?.userId}`
      )
    }
  }, [])

  const tour = useMemo(
    () => ({
      setIsOpen: setIsOpenCallback,
      setCurrentStep,
      setSteps: setStepsCallback,
      onNextStep,
      isGetStartedViewed: isGetStartedViewedCallback,
      setGetStartedViewed: setGetStartedViewedCallback,
      startTour,
      isSameSteps,
      setTourOpened,
      setStepsByActivityType,
      superIsOpen,
      isOpen,
      currentStep,
      steps,
      tourOpened,
      getGetStartedsViewed,
    }),
    [
      isOpen,
      currentStep,
      steps,
      tourOpened,
      forceTour,
      setIsOpen,
      superIsOpen,
      setCurrentStep,
      setSteps,
      setIsOpenCallback,
      onNextStep,
      getGetStartedsViewed,
    ]
  )

  return tour
}
